'use client';

import { c as _c } from "react/compiler-runtime";
import IconPlay from '@/components/assets/icon_play.svg';
import { useGradientAnimation } from '@/hooks/useGradientAnimation';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { useId } from 'react';
type PlayCircleIconProps = {
  className?: string;
  triangleClassName?: string;
};

// `gradient1` is `{gradients.play-icon-triangle}`; `gradient2` mirrors it, so the colours sweep
// across the triangle on the same 1s cadence as `AnimatedGradientButton`.
const gradientStops = [{
  offset: 0,
  gradient1: '#05ece1',
  gradient2: '#ff82fb'
}, {
  offset: 0.38453,
  gradient1: '#00b3e3',
  gradient2: '#00b3e3'
}, {
  offset: 1,
  gradient1: '#ff82fb',
  gradient2: '#05ece1'
}];

/** Brand-gradient play triangle on a round plate, as in the CONNECT-6141 reference. */
export default function PlayCircleIcon(t0) {
  const $ = _c(17);
  const {
    className,
    triangleClassName
  } = t0;
  const t1 = useId();
  let t2;
  if ($[0] !== t1) {
    let t3;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = /\W/g;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    t2 = t1.replace(t3, "");
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const gradientId = `play-circle-${t2}`;
  const controls = useGradientAnimation();
  let t3;
  if ($[3] !== className) {
    t3 = cn("flex shrink-0 items-center justify-center rounded-full", className);
    $[3] = className;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== controls) {
    t4 = gradientStops.map(t5 => {
      const {
        offset,
        gradient1,
        gradient2
      } = t5;
      return <motion.stop key={offset} offset={offset} initial="base" animate={controls} variants={{
        base: {
          stopColor: gradient1
        },
        gradient1: {
          stopColor: gradient1,
          transition: {
            duration: 1,
            ease: "easeOut"
          }
        },
        gradient2: {
          stopColor: gradient2,
          transition: {
            duration: 1,
            ease: "easeOut"
          }
        }
      }} />;
    });
    $[5] = controls;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== gradientId || $[8] !== t4) {
    t5 = <svg className="absolute size-0" focusable="false"><linearGradient id={gradientId} x1="1" y1="0" x2="0" y2="0">{t4}</linearGradient></svg>;
    $[7] = gradientId;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = `url(#${gradientId})`;
  let t7;
  if ($[10] !== t6 || $[11] !== triangleClassName) {
    t7 = <IconPlay fill={t6} className={triangleClassName} />;
    $[10] = t6;
    $[11] = triangleClassName;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t3 || $[14] !== t5 || $[15] !== t7) {
    t8 = <span aria-hidden={true} className={t3}>{t5}{t7}</span>;
    $[13] = t3;
    $[14] = t5;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}